import BaseApiClient from '../BaseApiClient';
import { useRelationshipTagsFixtures } from './config';
import {
  composeCatalogueFromApi,
  deriveTagKey,
  deriveTagKeyWithSuffix,
  isTagKeyValidationConflict,
  type ApiRelationshipTag,
  type ApiRelationshipTagCategory
} from './map-api';
import { RELATIONSHIP_TAGS_API_VERSION, relationshipTagsApiPaths } from './paths';
import {
  createFixtureCategory,
  createFixtureTag,
  deleteFixtureCategory,
  deleteFixtureTag,
  getFixtureCatalogue,
  renameFixtureTag,
  toggleFixtureTag,
  updateFixtureCategory
} from './storage';
import type {
  CreateCategoryPayload,
  CreateTagPayload,
  InsightTagsCatalogue,
  RenameTagPayload,
  UpdateCategoryPayload
} from './types';

export type {
  CreateCategoryPayload,
  CreateTagPayload,
  InsightTag,
  InsightTagCategory,
  InsightTagsCatalogue,
  InsightTagsSummary,
  RenameTagPayload,
  ToggleTagPayload,
  UpdateCategoryPayload
} from './types';

function unwrapData<T>(payload: { data?: T } | T): T {
  if (
    payload !== null &&
    typeof payload === 'object' &&
    'data' in payload &&
    (payload as { data?: T }).data !== undefined
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

/**
 * Relationship / AI insight tags catalogue.
 * Live HTTP by default; fixtures only when VITE_RELATIONSHIP_TAGS_USE_FIXTURES=true.
 *
 * Live mode composes Ross flat category + tag list endpoints into InsightTagsCatalogue
 * (no `/relationship-tags/catalogue` on BE yet).
 */
export class ApiRelationshipTags extends BaseApiClient {
  private withVersionHeader() {
    return { headers: { Version: RELATIONSHIP_TAGS_API_VERSION } };
  }

  async getCatalogue(): Promise<InsightTagsCatalogue> {
    if (useRelationshipTagsFixtures()) {
      return getFixtureCatalogue();
    }

    const [categoriesRes, activeTagsRes, inactiveTagsRes] = await Promise.all([
      this.client.get(relationshipTagsApiPaths.categories, this.withVersionHeader()),
      this.client.get(relationshipTagsApiPaths.tags, this.withVersionHeader()),
      this.client.get(relationshipTagsApiPaths.tags, {
        ...this.withVersionHeader(),
        params: { is_active: false }
      })
    ]);

    const categories = unwrapData<ApiRelationshipTagCategory[]>(
      categoriesRes.data
    );
    const activeTags = unwrapData<ApiRelationshipTag[]>(activeTagsRes.data);
    const inactiveTags = unwrapData<ApiRelationshipTag[]>(
      inactiveTagsRes.data
    );

    if (!Array.isArray(categories) || !Array.isArray(activeTags)) {
      throw new Error('Invalid relationship tags catalogue response');
    }

    const tags = [
      ...activeTags,
      ...(Array.isArray(inactiveTags) ? inactiveTags : [])
    ];

    return composeCatalogueFromApi(categories, tags);
  }

  async createCategory(
    payload: CreateCategoryPayload
  ): Promise<InsightTagsCatalogue> {
    if (useRelationshipTagsFixtures()) {
      return createFixtureCategory(payload);
    }
    await this.client.post(
      relationshipTagsApiPaths.categories,
      {
        name: payload.name,
        color_hex: payload.colour
      },
      this.withVersionHeader()
    );
    return this.getCatalogue();
  }

  async updateCategory(
    categoryId: string,
    payload: UpdateCategoryPayload
  ): Promise<InsightTagsCatalogue> {
    if (useRelationshipTagsFixtures()) {
      return updateFixtureCategory(categoryId, payload);
    }
    await this.client.patch(
      relationshipTagsApiPaths.category(categoryId),
      {
        name: payload.name,
        color_hex: payload.colour
      },
      this.withVersionHeader()
    );
    return this.getCatalogue();
  }

  async deleteCategory(categoryId: string): Promise<InsightTagsCatalogue> {
    if (useRelationshipTagsFixtures()) {
      return deleteFixtureCategory(categoryId);
    }
    await this.client.delete(
      relationshipTagsApiPaths.category(categoryId),
      this.withVersionHeader()
    );
    return this.getCatalogue();
  }

  async createTag(payload: CreateTagPayload): Promise<InsightTagsCatalogue> {
    if (useRelationshipTagsFixtures()) {
      return createFixtureTag(payload);
    }

    const body = {
      relationship_tag_category_id: payload.category_id,
      tag_name: payload.name,
      description: payload.description,
      tag_key: deriveTagKey(payload.name),
      is_active: true
    };

    try {
      await this.client.post(
        relationshipTagsApiPaths.tags,
        body,
        this.withVersionHeader()
      );
    } catch (error) {
      if (!isTagKeyValidationConflict(error)) {
        throw error;
      }
      await this.client.post(
        relationshipTagsApiPaths.tags,
        {
          ...body,
          tag_key: deriveTagKeyWithSuffix(payload.name)
        },
        this.withVersionHeader()
      );
    }

    return this.getCatalogue();
  }

  async renameTag(
    tagId: string,
    payload: RenameTagPayload
  ): Promise<InsightTagsCatalogue> {
    if (useRelationshipTagsFixtures()) {
      return renameFixtureTag(tagId, payload);
    }
    await this.client.patch(
      relationshipTagsApiPaths.tag(tagId),
      { tag_name: payload.name },
      this.withVersionHeader()
    );
    return this.getCatalogue();
  }

  async toggleTag(
    tagId: string,
    is_active: boolean
  ): Promise<InsightTagsCatalogue> {
    if (useRelationshipTagsFixtures()) {
      return toggleFixtureTag(tagId, is_active);
    }
    await this.client.patch(
      relationshipTagsApiPaths.tag(tagId),
      { is_active },
      this.withVersionHeader()
    );
    return this.getCatalogue();
  }

  async deleteTag(tagId: string): Promise<InsightTagsCatalogue> {
    if (useRelationshipTagsFixtures()) {
      return deleteFixtureTag(tagId);
    }
    await this.client.delete(
      relationshipTagsApiPaths.tag(tagId),
      this.withVersionHeader()
    );
    return this.getCatalogue();
  }
}
